import React, { useEffect, useState, useMemo, useRef } from "react";
import api from "../../api";
import "../../styles/Tab.css";

function TopCategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(new Set());
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    setCategories([]);
    setLoading(true);
    api
      .get("/v1/frontend/topCategories")
      .then((res) => {
        const rows = res.data?.data?.data || res.data?.data || res.data || [];
        setCategories(Array.isArray(rows) ? rows : []);
        setLoading(false);
      })
      .catch(() => {
        setCategories([]);
        setLoading(false);
      });
  }, []);

  const formatNumber = (v) => {
    if (v == null || v === "") return "";
    const n = Number(String(v).replace(/,/g, ""));
    if (Number.isNaN(n)) return String(v);
    return (
      "$" +
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n)
    );
  };

  const formatQuantity = (v) => {
    if (v == null || v === "") return "";
    const n = Number(String(v).replace(/,/g, ""));
    if (Number.isNaN(n)) return String(v);
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  };

  const formatDate = (v) => {
    if (v == null || v === "") return "";
    try {
      const date = new Date(v);
      if (isNaN(date.getTime())) return String(v);
      return new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch {
      return String(v);
    }
  };

  const exclude = useMemo(
    () =>
      new Set([
        "id",
        "company_id",
        "deleted_at",
        "created_at",
        "updated_at",
        "sync_at",
        "synced_at",
        "sync_status",
        "custom_fields",
        "meta",
      ]),
    []
  );

  const cols = useMemo(
    () =>
      categories && categories.length > 0
        ? Array.from(new Set(categories.flatMap(Object.keys))).filter(
            (k) => !exclude.has(k)
          )
        : [],
    [categories, exclude]
  );

  useEffect(() => {
    if (cols.length > 0 && visibleColumns.size === 0)
      setVisibleColumns(new Set(cols));
  }, [cols, visibleColumns.size]);

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categories;
    const q = String(searchTerm).toLowerCase();
    return categories.filter((s) =>
      Object.values(s || {}).some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [categories, searchTerm]);

  const visibleCols = useMemo(() => {
    const filtered = cols.filter((c) => visibleColumns.has(c));

    const salesAmountCols = filtered.filter((c) =>
      /sold_amount|soldamount|sales/i.test(c)
    );
    const otherCols = filtered.filter(
      (c) =>
        !/sold_amount|soldamount|sales|sold_qty|soldqty|quantity|qty|average_price|averageprice|avg_price|avgprice/i.test(
          c
        )
    );

    return [
      ...otherCols,
      ...salesAmountCols,
    ];
  }, [cols, visibleColumns]);
  const pagedRows = useMemo(
    () => filteredCategories.slice((page - 1) * pageSize, page * pageSize),
    [filteredCategories, page, pageSize]
  );
  const filteredColumns = useMemo(() => {
    if (!columnSearchTerm) return cols;
    const q = String(columnSearchTerm).toLowerCase();
    return cols.filter((c) => c.toLowerCase().includes(q));
  }, [cols, columnSearchTerm]);

  const toggleColumn = (column) =>
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return next;
    });
  const toggleAllColumns = () =>
    setVisibleColumns((prev) =>
      prev.size === cols.length ? new Set() : new Set(cols)
    );

  const pretty = (key) => {
    const columnMappings = {
      sold_amount: "Sales Amount",
      sold_qty: "Quantity",
      soldamount: "Sales Amount",
      soldqty: "Quantity",
    };

    const lowerKey = key.toLowerCase();
    if (columnMappings[lowerKey]) {
      return columnMappings[lowerKey];
    }

    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const SkeletonRow = () => (
    <tr className="skeleton-row">
      {Array.from({ length: 5 }).map((_, i) => (
        <td key={i}>
          <div className="skeleton-cell" />
        </td>
      ))}
    </tr>
  );

  return (
    <>
      <div className="topcategories-wrapper">
        <h2 className="customers-title">Top Categories</h2>
        {loading ? (
          <>
            <div className="table-controls">
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search categories..."
                  disabled
                  value={searchTerm}
                  className="search-input"
                />
                <span className="search-icon" aria-hidden>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14z"
                      stroke="#6b7280"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <path
                      d="M21 21l-4.35-4.35"
                      stroke="#6b7280"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button className="column-toggle-btn disabled" disabled>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="5"
                      y="6"
                      width="2"
                      height="10"
                      rx="1"
                      fill="#6b7280"
                    />
                    <rect
                      x="10"
                      y="3"
                      width="2"
                      height="13"
                      rx="1"
                      fill="#6b7280"
                    />
                    <rect
                      x="15"
                      y="9"
                      width="2"
                      height="7"
                      rx="1"
                      fill="#6b7280"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="table-container" ref={tableContainerRef}>
              {loading && (
                <div className="table-loading-overlay" aria-hidden>
                  <div className="table-loading-spinner" />
                </div>
              )}
              <table className="styled-table">
                <thead>
                  <tr>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <th key={i} className="table-header-cell">
                        <div className="skeleton-cell" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonRow key={i} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : filteredCategories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="#7B5EA3"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M12 17.3l4.9 2.6-1.3-5.5L19 11l-5.6-.5L12 5l-1.4 5.5L5 11l3.4 3.4-1.3 5.5L12 17.3z"
                  stroke="#7B5EA3"
                  strokeWidth="1.2"
                  fill="none"
                />
              </svg>
            </div>
            <h3>No category data found</h3>
            <p>
              {searchTerm
                ? `No results for "${searchTerm}". Try adjusting your search.`
                : "There are no categories to display."}
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="clear-search-btn"
                type="button"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="table-controls">
              <div className="search-container">
                <input
                  className="search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="search-icon" aria-hidden>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14z"
                      stroke="#6b7280"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <path
                      d="M21 21l-4.35-4.35"
                      stroke="#6b7280"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
              <div className="results-count">
                {filteredCategories.length} results
              </div>
              <div className="control-group">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="page-size-select"
                >
                  {[10, 20, 50, 100].map((s) => (
                    <option key={s} value={s}>
                      Show {s}
                    </option>
                  ))}
                </select>
                <div className="column-toggle-container">
                  <button
                    onClick={() => setShowColumnToggle((v) => !v)}
                    className="column-toggle-btn"
                  >
                    ☰
                  </button>
                  {showColumnToggle && (
                    <div className="column-toggle-dropdown">
                      <div className="column-toggle-header">
                        <span>Columns</span>
                        <button
                          onClick={toggleAllColumns}
                          className="toggle-all-btn"
                        >
                          {visibleColumns.size === cols.length
                            ? "Deselect All"
                            : "Select All"}
                        </button>
                      </div>
                      <div className="column-search-container">
                        <input
                          className="column-search-input"
                          placeholder="Search columns..."
                          value={columnSearchTerm}
                          onChange={(e) => setColumnSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="column-list">
                        {filteredColumns.length > 0 ? (
                          filteredColumns.map((col) => (
                            <label key={col} className="column-toggle-item">
                              <input
                                type="checkbox"
                                checked={visibleColumns.has(col)}
                                onChange={() => toggleColumn(col)}
                              />
                              <span>{col}</span>
                            </label>
                          ))
                        ) : (
                          <div className="no-columns-message">
                            No columns found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="table-container" ref={tableContainerRef}>
              <table className="styled-table">
                <thead>
                  <tr>
                    {visibleCols.map((c) => (
                      <th className={/(qty|quantity|sold_qty|soldqty|sales|amount|total)/i.test(c) ? "right" : ""} key={c}>{pretty(c)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row) => (
                    <tr key={row.id ?? JSON.stringify(row)}>
                      {visibleCols.map((c) => (
                        <td className={/(qty|quantity|sold_qty|soldqty|sales|amount|total)/i.test(c) ? "right" : ""} key={c}>
                          {/(qty|quantity|sold_qty|soldqty)/i.test(c)
                            ? formatQuantity(row[c])
                            : /sales|amount|total|count|cogs|profit|margin|revenue|cost|price/i.test(
                                c
                              )
                            ? formatNumber(row[c])
                            : /(date|created|updated|purchase_date|sale_date)/i.test(
                                c
                              )
                            ? formatDate(row[c])
                            : row[c]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="pagination-btn prev"
              >
                Previous
              </button>
              <div className="pagination-pages">
                {Array.from(
                  {
                    length: Math.min(
                      5,
                      Math.max(
                        1,
                        Math.ceil(filteredCategories.length / pageSize)
                      )
                    ),
                  },
                  (_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`pagination-page ${
                        page === i + 1 ? "active" : ""
                      }`}
                    >
                      {i + 1}
                    </button>
                  )
                )}
              </div>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page * pageSize >= filteredCategories.length}
                className="pagination-btn next"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default TopCategoriesTab;
